import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Home, Info, Trophy, LogOut } from 'lucide-react';
import { useAuth } from '../context/auth-context';
import { cn } from '../lib/utils';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Tancar menú al clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // No tancar si es clica el botó hamburguesa o dins del drawer
      if (
        menuRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    if (isOpen) {
      // Usar timeout per evitar que es tanqui immediatament quan s'obre
      const timeout = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeout);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Tancar menú al canviar de ruta
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const navItems = [
    { path: '/inici', label: 'Inici', icon: Home },
    { path: '/info', label: 'Info', icon: Info },
    { path: '/classificacio', label: 'Classificació', icon: Trophy },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-[80] bg-zinc-900/95 backdrop-blur-sm border-b border-zinc-800">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo/Títol - sempre visible */}
          <div className="shrink-0">
            <button
              onClick={() => navigate('/inici')}
              className="text-2xl sm:text-3xl font-bold text-white hover:text-zinc-300 transition-colors cursor-pointer"
            >
              Cartes 🃏
            </button>
          </div>

          {/* Menú desktop - visible a partir de md */}
          <div className="hidden md:flex md:items-center md:space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2",
                    isActive(item.path)
                      ? "bg-white text-black"
                      : "text-zinc-300 hover:text-white hover:bg-zinc-800"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* Botó hamburguesa - només mòbil */}
          <button
            ref={buttonRef}
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors z-50 relative"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
          </button>
        </div>
      </div>

      {/* Overlay per tancar al clicar fora - cobreix tot el contingut de la pàgina */}
      {isOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black z-[30]"
            onClick={() => setIsOpen(false)}
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: '#000000'
            }}
          />
        </>
      )}

      {/* Menú mòbil - drawer des de la dreta */}
      <div
        ref={menuRef}
        className={cn(
          "md:hidden fixed top-0 right-0 h-screen w-[85vw] max-w-sm bg-zinc-900 border-l border-zinc-800 shadow-2xl z-[70] transition-transform duration-300 ease-out flex flex-col",
          isOpen
            ? "translate-x-0"
            : "translate-x-full"
        )}
      >
        {/* Header del drawer */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b-2 border-zinc-700 bg-zinc-900 shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/inici')}
              className="text-2xl sm:text-3xl font-bold text-white hover:text-zinc-300 transition-colors cursor-pointer"
            >
              Cartes 🃏
            </button>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all"
            aria-label="Tancar menú"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contingut del drawer */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-4 pt-4 min-h-0 max-h-[calc(100vh-200px)]">
          <nav className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "w-full px-4 sm:px-5 py-4 sm:py-5 rounded-xl text-lg sm:text-xl font-medium transition-all flex items-center gap-4 sm:gap-5 text-left group",
                    isActive(item.path)
                      ? "bg-white text-black shadow-lg"
                      : "text-zinc-300 hover:text-white hover:bg-zinc-800/50"
                  )}
                >
                  <Icon className={cn(
                    "w-6 h-6 sm:w-7 sm:h-7 transition-colors shrink-0",
                    isActive(item.path) ? "text-black" : "text-zinc-400 group-hover:text-white"
                  )} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

      </div>
    </nav>
  );
};
